import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import PaymentSummary from '../PaymentSummary/PaymentSummary';
import { useNavigate } from 'react-router-dom';
import PersonalInformationForm from './PersonalInformationForm';
import { Medlemskapstype } from '../api/medlemskapAPI';
import datejs from 'date-and-time';
import { sortEnkeltmedlemskap } from '../MedlemskapType/MedlemskapTypeId';
import Enkeltmedlemskap from '../MedlemskapType/Enkeltmedlemskap';
import { PersonalInformation } from '../types/PersonalInformation';
import { ApiResource } from '../api/ApiResource';
import { useErrorPageRedirectIfFailed } from '../hooks';
import BackLink from '../BackLink';
import * as constants from '../constants';
import { calculateAge, getMedlemskapstypeById, medlemskapstypeIdFromAge } from '../Utils';
import globalStyles from '../App.module.scss';
import { PostalCodeResponse } from '../api/postalCodeAPI';

interface EnkeltmedlemPersonalInformationPageProps {
    postalCodesListResource: ApiResource<PostalCodeResponse>;
    medlemskapstypeListResource: ApiResource<Medlemskapstype[]>;
    formInput: PersonalInformation;
    setFormInput: Dispatch<SetStateAction<PersonalInformation>>;
    setAmountToPay: Dispatch<SetStateAction<number>>;
    discountCode: string;
    setDiscountCode: Dispatch<SetStateAction<string>>;
    discount: number;
    setDiscount: Dispatch<SetStateAction<number>>;
}

export default function EnkeltmedlemPersonalInformationPage({
    postalCodesListResource,
    medlemskapstypeListResource,
    formInput,
    setFormInput,
    setAmountToPay,
    discountCode,
    setDiscountCode,
    discount,
    setDiscount,
}: EnkeltmedlemPersonalInformationPageProps) {
    const navigate = useNavigate();
    const [activeMedlemskap, setActiveMedlemskap] = useState<Medlemskapstype | undefined>();
    useErrorPageRedirectIfFailed(medlemskapstypeListResource);

    useEffect(() => {
        window.scrollTo(0, 0);
        setDiscountCode('');
        setDiscount(0);
    }, [setDiscount, setDiscountCode]);

    useEffect(() => {
        if (activeMedlemskap) {
            setAmountToPay(activeMedlemskap?.price - discount);
        }
    }, [activeMedlemskap, setAmountToPay, discount]);

    useEffect(() => {
        if (
            datejs.isValid(formInput.dateOfBirth, 'DD.MM.YYYY') &&
            formInput.dateOfBirth.match(/^\d{2}\.\d{2}\.\d{4}$/) &&
            medlemskapstypeListResource.status === 'ok'
        ) {
            const calculatedAge = calculateAge(formInput.dateOfBirth);
            const id = medlemskapstypeIdFromAge(calculatedAge);
            setActiveMedlemskap(getMedlemskapstypeById(medlemskapstypeListResource.data, id));
        } else {
            setActiveMedlemskap(undefined);
        }
    }, [formInput.dateOfBirth, medlemskapstypeListResource]);

    if (medlemskapstypeListResource.status !== 'ok') {
        return null;
    }

    const medlemskapstypeList = medlemskapstypeListResource.data;

    const onSubmit = (): void => {
        setFormInput({
            ...formInput,
            phoneNumber: formInput.phoneNumber.replaceAll(' ', '').replace(/^\+47|^0047/, ''),
        });
        if (activeMedlemskap) {
            setAmountToPay(activeMedlemskap?.price - discount);
            navigate(`../betaling/${activeMedlemskap.id}`, { replace: true });
        }
    };

    return (
        <div>
            <BackLink destination={constants.LINK_TO_START_PAGE} />
            {activeMedlemskap ? (
                <div className={globalStyles.summary}>
                    <PaymentSummary
                        medlemskapstype={activeMedlemskap}
                        discountCode={discountCode}
                        discount={discount}
                        hideDescription={true}
                    />
                </div>
            ) : (
                <Enkeltmedlemskap
                    medlemskapstyper={medlemskapstypeList
                        .filter((medlemskap) => medlemskap.id.includes('Enkelt'))
                        .sort(sortEnkeltmedlemskap)}
                />
            )}
            <PersonalInformationForm
                postalCodesListResource={postalCodesListResource}
                formInput={formInput}
                setFormInput={setFormInput}
                onSubmit={onSubmit}
                activeMedlemskap={activeMedlemskap}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
                discount={discount}
                setDiscount={setDiscount}
            />
        </div>
    );
}
